import React from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../shared/components/forms/inputs/InputField';
import DisabledField from '../../../shared/components/forms/inputs/DisabledField';
import InfoTooltip from '../../../shared/components/forms/InfoTooltip';
import { ValidationErrors } from '../types/firstProcessing.types';

interface ProcessingParametersSectionProps {
  timeMinRaw: string;
  timeMaxRaw: string;
  tss: string;
  offset: string;
  onTssChange: (value: string) => void;
  onOffsetChange: (value: string) => void;
  validationErrors: ValidationErrors;
  isConst: boolean;
  anchorTime: string;
  onAnchorTimeChange: (value: string) => void;
}

const ProcessingParametersSection: React.FC<ProcessingParametersSectionProps> = ({
  timeMinRaw,
  timeMaxRaw,
  tss,
  offset,
  onTssChange,
  onOffsetChange,
  validationErrors,
  isConst,
  anchorTime,
  onAnchorTimeChange
}) => {
  const { t } = useTranslation('firstProcessing');
  const { t: tUpload } = useTranslation('dataUpload');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Section Header */}
      <div className="border-b pb-2">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-foreground">
          {t('parameters.title')}
        </h3>
        {t('parameters.description') && (
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {t('parameters.description')}
          </p>
        )}
      </div>

      {/* Time Information Display */}
      {(timeMinRaw || timeMaxRaw) && (
        <div className="bg-accent border border-primary/20 rounded-lg p-3 sm:p-4">
          <h4 className="text-xs sm:text-sm font-medium text-accent-foreground mb-2 sm:mb-3">
            {t('timeInfo.title')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <DisabledField
              label={t('timeInfo.timeMinRaw')}
              value={timeMinRaw || '-'}
            />
            <DisabledField
              label={t('timeInfo.timeMaxRaw')}
              value={timeMaxRaw || '-'}
            />
          </div>
        </div>
      )}

      {/* Parameter Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* TSS Input */}
        <div>
          <div className="flex items-start mb-1 sm:mb-2 min-h-[2rem] sm:min-h-[2.5rem] md:min-h-[3rem]">
            <label className="block text-foreground font-medium text-xs sm:text-sm md:text-base">
              {t('inputParameters.tss')}
            </label>
            <InfoTooltip text={tUpload('tooltips.zeitschrittweite')} />
          </div>
          <InputField
            label=""
            value={tss}
            onChange={onTssChange}
            skipValidation={true}
          />
          {validationErrors.tss && (
            <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {validationErrors.tss}
            </p>
          )}
        </div>

        {/* Offset / Anchor-Time Input (conditional on isConst) */}
        {tss !== '' && (isConst ? (
          <div>
            <div className="flex items-start mb-1 sm:mb-2 min-h-[2rem] sm:min-h-[2.5rem] md:min-h-[3rem]">
              <label className="block text-foreground font-medium text-xs sm:text-sm md:text-base">
                {t('inputParameters.offsetConst')}
              </label>
              <InfoTooltip text={tUpload('tooltips.offset')} />
            </div>
            <InputField
              label=""
              value={offset}
              onChange={onOffsetChange}
              skipValidation={true}
            />
            {validationErrors.offset && (
              <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors.offset}
              </p>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-3 sm:mb-4">
              <div className="flex items-start mb-1 sm:mb-2 min-h-[2rem] sm:min-h-[2.5rem] md:min-h-[3rem]">
                <label className="block text-foreground font-medium text-xs sm:text-sm md:text-base">
                  {t('inputParameters.anchorTime')}
                </label>
                <InfoTooltip text={tUpload('tooltips.anchorTime')} />
              </div>
              <input
                type="text"
                value={anchorTime}
                onChange={(e) => onAnchorTimeChange(e.target.value)}
                placeholder="2025-08-15 06:30:00"
                className="w-full px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-md text-xs sm:text-sm md:text-base transition-all duration-200 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 focus:outline-none hover:border-yellow-400"
              />
            </div>
            {anchorTime && !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(anchorTime) && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">
                {t('inputParameters.anchorTimeFormatError')}
              </p>
            )}
          </div>
        ))}
      </div>

    </div>
  );
};

export default ProcessingParametersSection;